import { mapCountryObjForFilters } from '@components/multi-banner-retailer-selector/MultiBannerRetailerSelectorUtils';
import { isValidPageToDisplayPeriod } from '@components/time-frame-picker/components/wrapper/WrapperUtils';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { SortDirection, SortingOptions } from '@components/design/migrated/Table/Sorting/types';
import { ClientSubscription, Country } from '@models/Opa';
import { ProductDataType, ProductType } from '@pages/product-analyzer/Product';
import { getFeatureValueByPGAndCountry } from '@services/FeatureValue.service';
import { getFilters } from '@services/Filters.service';
import { BaseTimePeriod, FilterParam, MainFilters } from '@services/FiltersTypes';
import { getProductFeatures } from '@services/ProductFeatures.service';
import { getProductGroupsByCountry } from '@services/ProductGroup.service';
import { MainFiltersState } from '@state/reducers/main-filter/MainFiltersReducer';
import { SideFiltersState } from '@state/reducers/side-filters/SideFiltersReducer';
import {
  Country as PagesConfigCountry,
  FilterKeyTitleAndExclusion,
  FilterObject,
  FilterTitle,
  PageName,
  PageType,
  PageTypeAndName,
  ProductGroupPriceBandFeatureValue,
  SavedFilters,
} from '@utils/pageConfig/Types';
import { getIsDisabledStatus } from '@utils/productGroupUtils/ProductGroupUtils';
import { cloneDeep, isEqual, startCase, uniqBy } from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import {
  findSubscriptionByCountry,
  getSubscriptionDetailsByProductType,
  SubscriptionDetail,
  validateFilters,
} from './AppSubscriptionUtils';
import { filterPGsByCountry, getCountryFilter, updateCountryAndPGFilters } from '@utils/CountryUtils';
import {
  getBaseFilterKeysByPageTypeAndPageName,
  opportunityReviewResponseConfig,
  productsResponseConfig,
  sortOptionsFromApi,
  tradingAssistantResponseConfig,
} from './FilterSettings';
import { getMergeAndUniqObjectArrayByKey } from './ObjectUtils';
import { getDefaultValueForDerivablePeriodicity, isPeriodYTDOrMAT, isWeeklyYTDOrMAT } from '@utils/PeriodUtils';
import { isAuthorized } from './Permission';
import { NO_OPTIONS } from './StringConstants';
import {
  DEFAULT_CURRENCY_DROPDOWN_COUNTRIES,
  findCountryFromDefaultCurrenciesByCountryOption,
  getCurrencyOption,
  getCurrencyOptionsFromSubscription,
  isLocalCurrencyApplied,
} from './currencyUtils/CurrencyUtils';
import { defaultOneOptionForCountry } from './filterTrayUtils/FilterTrayUtils';
import { checkIsMultiCountryClient } from './multiCountryUtils/MultiCountryUtils';
import { byFilterKey } from './pageConfig/Predicates/SelectedFiltersPredicates';
import {
  CountryFilterPair,
  FeatureOption,
  FilterKeys,
  Filters,
  Option,
  ProductGroups,
  ResponseConfig,
  SelectedFilters,
  SelectedPgCheckParam,
} from './pageConfig/Types';
import { DisplayTypesOption } from '@components/top-selling-products/products-table-filter/Types';
import { PagesConfig } from './pageConfig/PagesConfigContext';

export const initialFeature: FeatureOption = {
  label: 'Brand',
  value: 'Brand',
  dataType: 'BRAND',
  feature: 'Brands',
  filterKey: FilterKeys.BRAND,
};

export interface RetailerDetails {
  clientId: number;
  productType: ProductType;
  productDataType: ProductDataType;
  selectedCountries: Option[];
  timeFrame: Periodicity;
  selectedProductGroup: string;
  currentTotalName: string;
  pageType?: number;
}

export interface RetailerDetailsTA {
  clientId: number;
  productType: ProductType;
  productDataType: ProductDataType;
  selectedCountries: Option[];
  timeFrame: Periodicity;
  selectedProductGroups: Option[];
  currentTotalName: string;
  pageType?: number;
}

export const updateSelectedPgCountryState = (
  selectedFilters: Filters[],
  selectedCountries: Option[],
  setSelectedCountries: Dispatch<SetStateAction<Option[]>>,
  setSelectedProductGroup: Dispatch<SetStateAction<Option[] | undefined>>,
) => {
  const selectedIndex = selectedFilters.findIndex((e) => e.filterKey === FilterKeys.COUNTRY);

  const selectedCountryFromFilter = selectedIndex !== -1 ? selectedFilters[selectedIndex].options : [];

  if (!areOptionsTheSame(selectedCountries, selectedCountryFromFilter, 'value')) {
    setSelectedCountries(selectedCountryFromFilter);
  }

  const pgIndex = selectedFilters.findIndex((e) => e.filterKey === FilterKeys.PRODUCT_GROUP);
  setSelectedProductGroup(selectedFilters[pgIndex]?.options);
};

type SelectedSegFeatValuesParam = {
  productType: ProductType;
  selectedProductGroup: Option[] | undefined;
  selectedSegFeatValues: Option[] | undefined;
  selectedSegFeat: string | undefined;
  initialFilters: MainFilters[];
  selectedCountries: Option[];
  pagesConfig: PagesConfig;
  selectedTimeFrame: Periodicity;
};

export const getSelectedSegFeatValues = async (param: SelectedSegFeatValuesParam) => {
  const {
    productType,
    selectedProductGroup,
    selectedSegFeatValues,
    selectedSegFeat,
    initialFilters,
    selectedCountries,
    pagesConfig,
    selectedTimeFrame,
  } = param;

  if (
    selectedProductGroup !== undefined &&
    selectedSegFeatValues !== undefined &&
    selectedSegFeat !== undefined &&
    selectedSegFeat.length > 0 &&
    selectedSegFeatValues.length > 0
  ) {
    const mainFilters = initialFilters.find((e) => e.country === selectedCountries[0].value);

    if (mainFilters !== undefined) {
      try {
        const featureValues = await getProductFeatures({
          clientId: pagesConfig.clientId,
          productType: productType,
          country: selectedCountries[0].value,
          periodType: selectedTimeFrame,
          productGroup: selectedProductGroup[0].value,
          dataType: 'SEGFEAT',
          feature: selectedSegFeat.toUpperCase(),
          featureValue: selectedSegFeatValues,
        });

        if (featureValues.length === 0) {
          pagesConfig.setAddFeatures([null]);
        } else {
          pagesConfig.setAddFeatures(featureValues);
        }
      } catch (error) {
        console.error(error);
        pagesConfig.setAddFeatures(undefined);
      }
    }
  }

  if (selectedSegFeatValues === undefined) {
    pagesConfig.setAddFeatures(undefined);
  }
};

type SelectedAddFeatValuesParam = {
  productType: ProductType;
  selectedProductGroup: Option[] | undefined;
  selectedAddFeatValues: Option[] | undefined;
  selectedAddFeat: string | undefined;
  initialFilters: MainFilters[];
  selectedCountries: Option[];
  pagesConfig: PagesConfig;
  selectedTimeFrame: Periodicity;
};

export const getSelectedAddFeatValues = async (param: SelectedAddFeatValuesParam) => {
  const {
    productType,
    selectedProductGroup,
    selectedAddFeatValues,
    selectedAddFeat,
    initialFilters,
    selectedCountries,
    pagesConfig,
    selectedTimeFrame,
  } = param;

  if (
    selectedProductGroup !== undefined &&
    selectedAddFeatValues !== undefined &&
    selectedAddFeat !== undefined &&
    selectedAddFeat.length > 0 &&
    selectedAddFeatValues.length > 0
  ) {
    const mainFilters = initialFilters.find((e) => e.country === selectedCountries[0].value);

    if (mainFilters !== undefined) {
      try {
        const featureValues = await getProductFeatures({
          clientId: pagesConfig.clientId,
          productType: productType,
          country: selectedCountries[0].value,
          periodType: selectedTimeFrame,
          productGroup: selectedProductGroup[0].value,
          dataType: 'ADDFEAT',
          feature: selectedAddFeat.toUpperCase(),
          featureValue: selectedAddFeatValues,
        });

        if (featureValues.length === 0) {
          pagesConfig.setSegFeatures([null]);
        } else {
          pagesConfig.setSegFeatures(featureValues);
        }
      } catch (error) {
        pagesConfig.setSegFeatures(undefined);
        throw error;
      }
    }
  }

  if (selectedAddFeatValues === undefined) {
    pagesConfig.setSegFeatures(undefined);
  }
};

export const getFeatureValueForRetailerTA = async ({
  clientId,
  productType,
  productDataType,
  selectedCountries,
  timeFrame,
  selectedProductGroups,
  currentTotalName,
  pageType,
}: RetailerDetailsTA) => {
  const featureValues = await getFeatureValueByPGAndCountry({
    clientId,
    productType,
    productDataType,
    country: selectedCountries.map((items) => items.value),
    periodType: timeFrame,
    productGroups: selectedProductGroups.map((items) => items.value),
    currentTotalName,
    includeCommonFeaturesOnly: true,
  });
  //Once we received the values pass to this function to set into side menu
  if (pageType === PageType.TRADING_ASSISTANT) {
    setSideMenuFilters(featureValues, tradingAssistantResponseConfig);
  }
  return featureValues;
};

export const getFeatureValueForRetailer = async ({
  clientId,
  productType,
  productDataType,
  selectedCountries,
  timeFrame,
  selectedProductGroup,
  currentTotalName,
  pageType,
}: RetailerDetails) => {
  const featureValues = await getFeatureValueByPGAndCountry({
    clientId,
    productType,
    productDataType,
    country: selectedCountries.map((countries) => countries.value),
    periodType: timeFrame,
    productGroups: [selectedProductGroup],
    currentTotalName,
  });
  //Once we received the values pass to this function to set into side menu
  if (pageType === PageType.TRADING_ASSISTANT) {
    setSideMenuFilters(featureValues, tradingAssistantResponseConfig);
  }
  setSideMenuFilters(featureValues, opportunityReviewResponseConfig);
  return featureValues;
};

/**
 * Sets the side menu filters via updating the responseConfig.
 * @param features List of Filter values received from API
 * @param responseConfig opportunityReviewResponseConfig
 */
const setSideMenuFilters = (features: Filters[], responseConfig: ResponseConfig) => {
  if (responseConfig.response.length >= 2) {
    // Market definition filters
    responseConfig.response[1].filters = cloneDeep(features.slice(0, 4));
  }
  if (responseConfig.response.length >= 3) {
    // Product specification filters

    responseConfig.response[2].filters = cloneDeep(features.slice(4, 6));

    // if has subgroup
    if (responseConfig.response[2].hasSubGroup) {
      // Product features filters
      responseConfig.response[2].subGroupFilters[0].filters = cloneDeep(features.slice(6));
    }
  }
};

export const getFeatureValueBySubscription = async (
  param: FilterParam,
  timeFrame: Periodicity,
  currentTotalName: string,
  signal?: AbortSignal,
  onError?: () => void,
  featureValues?: any,
) => {
  const mainFilters = param.initialFilters.find((e) => e.country === param.selectedCountries[0]?.value);
  let fetchedFeatureValues;

  if (mainFilters !== undefined) {
    const timeFrames = getSubscriptionPeriodicitiesByCountryAndProductType(
      param.productType,
      param.pagesConfig,
      param.selectedCountries,
    );

    /*
     * param.timeFrame is hard coded to a certain value when this method is called.
     * so, eg. if param.timeFrame was Weekly, and the subscription check returns Monthly,
     * means that Weekly does not exist, and we should not call the API again.
     * So early return.
     */
    if (!timeFrames.some((t) => t === param.timeFrame)) {
      return undefined;
    }

    fetchedFeatureValues = await fetchFeatureValuesIfNeeded(featureValues, param, currentTotalName, signal);

    if (
      onError &&
      (fetchedFeatureValues.length === 0 ||
        (fetchedFeatureValues.length > 0 &&
          isAllFeatureValuesEmpty(fetchedFeatureValues) &&
          isAppliedFiltersEqualToSelectedFilters(param.pagesConfig)))
    ) {
      onError();
    }

    // Only replace the product features in the side menu when it matches currently selected timeFrame
    if (
      param.timeFrame &&
      param.timeFrame === getDefaultValueForDerivablePeriodicity(timeFrame) &&
      fetchedFeatureValues.length !== 0
    ) {
      setSideMenuFilters(fetchedFeatureValues, param.responseConfig);
    }
    param.pagesConfig.setSelectedPgUpdated(true);
  }

  return fetchedFeatureValues;
};

const fetchFeatureValuesIfNeeded = async (
  featureValues: any,
  param: FilterParam,
  currentTotalName: string,
  signal?: AbortSignal,
) => {
  if (featureValues) {
    return featureValues;
  }

  const { pagesConfig, productType, selectedCountries, timeFrame, selectedProductGroup, includeCommonFeaturesOnly } =
    param;

  return getFeatureValueByPGAndCountry({
    clientId: pagesConfig.clientId,
    productType,
    productDataType: ProductDataType.PerformanceAnalyzer,
    country: selectedCountries.map((country) => country.value),
    periodType: timeFrame!,
    productGroups: selectedProductGroup?.[0] ? selectedProductGroup.map(({ value }) => value) : [],
    currentTotalName,
    signal,
    includeCommonFeaturesOnly,
  });
};

export const isAppliedFiltersEqualToSelectedFilters = (pagesConfig: PagesConfig): boolean => {
  return (
    JSON.stringify(pagesConfig.appliedFilters[pagesConfig.pageType].selectedFilters) ===
    JSON.stringify(pagesConfig.selectedFilters[pagesConfig.pageType].selectedFilters)
  );
};

export const getCountriesByAccessibleProductGroup = (
  pagesConfig: PagesConfig,
  periodicity: Periodicity,
  countries: Option[],
  productGroups: Option[] | undefined,
) => {
  if (countries.length === 0 || productGroups?.length === 0) {
    return countries;
  }

  const countriesByAccessibleProductGroup: Option[] = [];
  countries.forEach((appliedCountry) => {
    const countryAccessibleProductGroups = pagesConfig.pgsByCountry.find(
      ({ country, periodType }) =>
        country === appliedCountry.value &&
        periodType === getDefaultValueForDerivablePeriodicity(periodicity).toUpperCase(),
    );
    if (countryAccessibleProductGroups) {
      if (countryAccessibleProductGroups.filter.options.some((option) => option.value === productGroups?.[0].value)) {
        countriesByAccessibleProductGroup.push(appliedCountry);
      }
    }
  });

  return countriesByAccessibleProductGroup;
};

export type UpdateAddFeatSegFeatValues = {
  setSelectedAddFeat: Dispatch<SetStateAction<string | undefined>>;
  setSelectedAddFeatValues: Dispatch<SetStateAction<Option[] | undefined>>;
  setSelectedSegFeat: Dispatch<SetStateAction<string | undefined>>;
  setSelectedSegFeatValues: Dispatch<SetStateAction<Option[] | undefined>>;
};

export const updateSelectedProductGroup = (
  pagesConfig: PagesConfig,
  selectedCountries: Option[],
  setSelectedCountries: Dispatch<SetStateAction<Option[]>>,
  setSelectedProductGroup: Dispatch<SetStateAction<Option[] | undefined>>,
  updateAddFeatSegFeatValues: UpdateAddFeatSegFeatValues,
) => {
  const selectedFilters = [...(pagesConfig.selectedFilters[pagesConfig.pageType]?.selectedFilters || [])];

  if (selectedFilters.length !== 0) {
    updateSelectedPgCountryState(selectedFilters, selectedCountries, setSelectedCountries, setSelectedProductGroup);

    const addFeatIndex = selectedFilters.findIndex((e) => e.filterKey === FilterKeys.ADDFEAT);
    if (addFeatIndex !== -1) {
      updateAddFeatSegFeatValues.setSelectedAddFeat(selectedFilters[addFeatIndex].filterTitle);
      updateAddFeatSegFeatValues.setSelectedAddFeatValues(selectedFilters[addFeatIndex].options);
    } else {
      updateAddFeatSegFeatValues.setSelectedAddFeat(undefined);
      updateAddFeatSegFeatValues.setSelectedAddFeatValues(undefined);
    }

    const segFeatIndex = selectedFilters.findIndex((e) => e.filterKey === FilterKeys.SEGFEAT);
    if (segFeatIndex !== -1) {
      updateAddFeatSegFeatValues.setSelectedSegFeat(selectedFilters[segFeatIndex].filterTitle);
      updateAddFeatSegFeatValues.setSelectedSegFeatValues(selectedFilters[segFeatIndex].options);
    } else {
      updateAddFeatSegFeatValues.setSelectedSegFeat(undefined);
      updateAddFeatSegFeatValues.setSelectedSegFeatValues(undefined);
    }
  }
};

export const updateSelectedProductGroupForProducts = (
  pagesConfig: PagesConfig,
  selectedCountries: Option[],
  setSelectedCountries: Dispatch<SetStateAction<Option[]>>,
  setSelectedProductGroup: Dispatch<SetStateAction<Option[] | undefined>>,
) => {
  const selectedFilters = [...pagesConfig.selectedFilters[PageType.RI_PRODUCT].selectedFilters];

  if (selectedFilters.length !== 0) {
    updateSelectedPgCountryState(selectedFilters, selectedCountries, setSelectedCountries, setSelectedProductGroup);
  }
};

export const updateFieldsForOverviewAndCategoryLeaderboardAndExplore = (
  pagesConfig: PagesConfig,
  selectedCountries: Option[],
  setSelectedCountries: Dispatch<SetStateAction<Option[]>>,
  setSelectedProductGroup: Dispatch<SetStateAction<Option[] | undefined>>,
  updateSideFilters: (payload: SideFiltersState) => (dispatch: any) => void,
) => {
  const selectedFilters = [...pagesConfig.selectedFilters[pagesConfig.pageType].selectedFilters];
  const hasCountryFilter = selectedFilters.some(byFilterKey(FilterKeys.COUNTRY));
  if (hasCountryFilter) {
    updateSelectedPgCountryState(selectedFilters, selectedCountries, setSelectedCountries, setSelectedProductGroup);
  }
  // This code only adds the Country FilterKey to Overview/CategoryLeaderboard pages on first
  // load to prevent the filter chips from disappearing and avoiding an incorrect saved view due to
  // a missing country filter when accessing appliedFilters.
  const emptySelectedCountryFilter = {
    dataType: 'COUNTRY',
    feature: 'COUNTRY',
    filterTitle: 'Country',
    filterKey: 1,
    options: [],
    disabled: false,
  };
  defaultOneOptionForCountry(
    pagesConfig,
    updateSideFilters,
    emptySelectedCountryFilter,
    getInitialAppliedCountries(pagesConfig, pagesConfig.pageType, ProductType.PerformanceAnalyzer),
    pagesConfig.pageType, // i.e. Retailer Overview, CategoryLeaderboard
  );
};

export const getPeriodForFeatures = (
  pagesConfig: PagesConfig,
  currTimeFrame: Periodicity,
  selectedCountries: Option[],
): Periodicity => {
  const timeFrames: Periodicity[] = getSubscriptionPeriodicitiesByCountryAndProductType(
    ProductType.PerformanceAnalyzer,
    pagesConfig,
    selectedCountries,
  );
  if (!timeFrames.some((period: Periodicity) => period === currTimeFrame)) {
    return timeFrames[0];
  }

  return currTimeFrame;
};

export const getFiltersInitialState = (allFilters: Filters[], filterKey: FilterKeys): Filters | undefined => {
  return allFilters.find((e) => e.filterKey === filterKey);
};

export const getInitialAppliedCountries = (
  pagesConfig: PagesConfig,
  pageType: PageType,
  productType: ProductType,
): Option[] => {
  const allFilters = [...(pagesConfig.appliedFilters[pageType]?.selectedFilters ?? [])];
  const countriesInitialState = getFiltersInitialState(allFilters, FilterKeys.COUNTRY);

  const subscriptionDetails = getSubscriptionDetailsByProductType(productType, pagesConfig);
  const currentClientCountries = subscriptionDetails.currentClientCountries;

  const allCountryOptions = currentClientCountries.map((x) => {
    return {
      label: x.name,
      value: x.id,
    };
  });
  const defaultCountry = allCountryOptions.slice(0, 1);
  if (countriesInitialState) {
    //Checks if the current applied country exist in the page subscription's country list
    //If it doesnt exist, return a default country.
    if (allCountryOptions.some((countryOption) => countryOption.value === countriesInitialState.options[0].value)) {
      return countriesInitialState.options;
    }
  }
  return defaultCountry;
};

export const addToUpdateFilter = (curr: SelectedFilters[], pageType: PageType, toUpdateFilter: Filters) => {
  const newSelectedFilters = [...curr];
  const filterByPageType = newSelectedFilters[pageType].selectedFilters;
  updateAppliedFilters(toUpdateFilter, filterByPageType);
  return newSelectedFilters;
};

export const getInitialSelectedAddFeat = (addFeatInitialState: Filters | undefined): string | undefined => {
  return addFeatInitialState !== undefined ? addFeatInitialState.filterTitle : undefined;
};

export const getInitialSelectedAddFeatValues = (addFeatInitialState: Filters | undefined): Option[] | undefined => {
  return addFeatInitialState !== undefined ? addFeatInitialState.options : undefined;
};

export const getInitialSelectedSegFeat = (segFeatInitialState: Filters | undefined): string | undefined => {
  return segFeatInitialState !== undefined ? segFeatInitialState.filterTitle : undefined;
};

export const getInitialSelectedSegFeatValues = (segFeatInitialState: Filters | undefined): Option[] | undefined => {
  return segFeatInitialState !== undefined ? segFeatInitialState.options : undefined;
};

/**
 * To reset previous applied side filters for multi country when the user switches between multi country and single country
 *
 * For page that shared applied side filters data for page type OPPORTUNITY REVIEW but doesn't have multi country feature
 * (example: OR and TA page), the applied countries will get sliced to single country in here as well when navigating
 * to respective pages
 *
 * Also set the visibility for filter chips clear all buttons based on multi country selection and other filters
 * that are not based filters
 *
 * @param storeFilterSettings - The current state of the side filters.
 * @param pageTypeAndName - Current page type and page name
 * @param pagesConfig - pagesConfig
 *
 * */
export const resetAppliedMultiCountryFilter = (
  storeFilterSettings: SideFiltersState,
  pageTypeAndName: PageTypeAndName,
  pagesConfig: PagesConfig,
) => {
  const { pageType, pageName } = pageTypeAndName;

  let appliedFiltersToSplice = storeFilterSettings.appliedFilters[pageType].selectedFilters;

  const countryFilterIndex = appliedFiltersToSplice.findIndex(byFilterKey(FilterKeys.COUNTRY));

  if (countryFilterIndex !== -1) {
    if (pageName && [PageName.OPPORTUNITY_REVIEW, PageName.TRADING_ASSISTANT, PageName.EXPLORE].includes(pageName)) {
      appliedFiltersToSplice[countryFilterIndex].options.splice(
        1,
        appliedFiltersToSplice[countryFilterIndex].options.length - 1,
      );
    } else if (
      pagesConfig.multiCountryClientCountries.length > 0 &&
      isAllSavedMultiCountryFilterExistInCurrentSubscription(pagesConfig)
    ) {
      const defaultCountry: Filters = getCountryFilter(pagesConfig.multiCountryClientCountries);
      appliedFiltersToSplice.splice(countryFilterIndex, 1, defaultCountry);
    }
  }
};

export const validateStoreFilterSettings = (
  storeFilterSettings: SideFiltersState,
  pagesConfig: PagesConfig,
  pageTypeAndName: PageTypeAndName,
) => {
  if (storeFilterSettings === null || !isBaseFiltersApplied(pageTypeAndName, storeFilterSettings.appliedFilters)) {
    return;
  }

  if (
    [PageType.EXPLORE, PageType.OPPORTUNITY_REVIEW].includes(pageTypeAndName.pageType) &&
    checkIsMultiCountryClient(pagesConfig.clientId)
  ) {
    resetAppliedMultiCountryFilter(storeFilterSettings, pageTypeAndName, pagesConfig);
  }

  pagesConfig.setAppliedFilters(storeFilterSettings.appliedFilters);
  pagesConfig.setSelectedFilters(storeFilterSettings.appliedFilters);
};

export const isValidTimeFrameAndPeriod = (timeFrame: Periodicity, period: BaseTimePeriod): boolean => {
  if (!timeFrame || !period?.label || period.label.includes('NAN')) {
    return false;
  }

  const label = period.label.toLowerCase();
  return !(
    (timeFrame === Periodicity.Weekly && !label.includes('week')) ||
    (timeFrame === Periodicity.Monthly && label.includes('week'))
  );
};

/**
 * Verifies whether only the base filters are applied or selected based on page.
 * @param pageTypeAndName current page type and page name
 * @param pagesFilters All filters, typically pagesConfig.selectedFilters or pagesConfig.appliedFilters
 * @param isMultiCountry to determine whether multi-country is selected
 * @returns boolean
 */
export const isOnlyBaseFiltersApplied = (
  pageTypeAndName: PageTypeAndName,
  pagesFilters: SelectedFilters[],
  isMultiCountry?: boolean,
): boolean => {
  const baseFilterKeys: FilterKeys[] = getBaseFilterKeysByPageTypeAndPageName(
    pageTypeAndName,
    pagesFilters[pageTypeAndName.pageType].selectedFilters,
    isMultiCountry,
  );
  return (
    pagesFilters[pageTypeAndName.pageType].selectedFilters.length === baseFilterKeys.length &&
    isBaseFiltersApplied(pageTypeAndName, pagesFilters, isMultiCountry)
  );
};

/**
 * Determines whether the base filters are applied/selected based on page.
 *
 * @param pageTypeAndName - The current page.
 * @param pagesFilters - The selected filters for each page type, typically `pagesConfig.selectedFilters` or `pagesConfig.appliedFilters`.
 * @param isMultiCountry
 * @returns A boolean indicating whether the base filters are applied.
 */
export const isBaseFiltersApplied = (
  pageTypeAndName: PageTypeAndName,
  pagesFilters: SelectedFilters[],
  isMultiCountry?: boolean,
): boolean => {
  const { pageType } = pageTypeAndName;
  const baseFilterKeys: FilterKeys[] = getBaseFilterKeysByPageTypeAndPageName(
    pageTypeAndName,
    pagesFilters[pageType].selectedFilters,
    isMultiCountry,
  );

  const appliedBaseFilters = pagesFilters[pageType].selectedFilters.filter((sideFilter) =>
    baseFilterKeys.includes(sideFilter.filterKey),
  );

  const hasCurrencyFilter = appliedBaseFilters.some(byFilterKey(FilterKeys.CURRENCY));
  const hasCountryFilter = appliedBaseFilters.some(byFilterKey(FilterKeys.COUNTRY));
  const hasProductGroupFilter = appliedBaseFilters.some(byFilterKey(FilterKeys.PRODUCT_GROUP));
  const hasBrandFilter = appliedBaseFilters.some(byFilterKey(FilterKeys.BRAND));

  const allBaseFiltersApplied = appliedBaseFilters.length === baseFilterKeys.length;

  switch (pageType) {
    case PageType.RETAILER_OVERVIEW:
    case PageType.OPPORTUNITY_REVIEW:
    case PageType.CATEGORY_LEADERBOARD:
      return (hasCurrencyFilter && hasCountryFilter) || allBaseFiltersApplied;

    case PageType.EXPLORE:
    case PageType.PERFORMANCE_ANALYSIS:
      return (
        (hasCurrencyFilter && hasCountryFilter) || (hasCurrencyFilter && hasProductGroupFilter) || allBaseFiltersApplied
      );

    case PageType.TRADING_ASSISTANT:
      return (
        (hasBrandFilter && hasCountryFilter) ||
        (hasCurrencyFilter && hasCountryFilter) ||
        (hasBrandFilter && hasCountryFilter && hasProductGroupFilter) ||
        (hasCurrencyFilter && hasCountryFilter && hasProductGroupFilter) ||
        allBaseFiltersApplied
      );

    case PageType.RI_PRODUCT:
      return (hasCurrencyFilter && hasCountryFilter && hasProductGroupFilter) || allBaseFiltersApplied;

    default:
      return allBaseFiltersApplied;
  }
};

/**
 * Verifies whether the base filters, along with other filters, are applied or selected based on page.
 * @param pageTypeAndName current page type and page name
 * @param pagesFilters All filters, typically pagesConfig.selectedFilters or pagesConfig.appliedFilters
 * @param isMultiCountry to determine whether multi-country is selected
 * @returns boolean
 */
export const isBaseFiltersWithAtLeastOneOtherFilterApplied = (
  pageTypeAndName: PageTypeAndName,
  pagesFilters: SelectedFilters[],
  isMultiCountry?: boolean,
): boolean => {
  const baseFilterKeys: FilterKeys[] = getBaseFilterKeysByPageTypeAndPageName(
    pageTypeAndName,
    pagesFilters[pageTypeAndName.pageType].selectedFilters,
    isMultiCountry,
  );

  return (
    pagesFilters[pageTypeAndName.pageType].selectedFilters.length > baseFilterKeys.length &&
    isBaseFiltersApplied(pageTypeAndName, pagesFilters)
  );
};

/**
 * Determines if there are base filters with at least one other filter applied within a saved
 * filter.
 *
 * @param pageTypeAndName - The page type and name.
 * @param filters - The filters to check.
 * @param isMultiCountry
 */
export const isSavedFilterContainBaseFilterWithOtherFilters = (
  pageTypeAndName: PageTypeAndName,
  filters: Filters[],
  isMultiCountry?: boolean,
) => {
  const baseFilterKeys: FilterKeys[] = getBaseFilterKeysByPageTypeAndPageName(pageTypeAndName, filters, isMultiCountry);

  const baseFilterKeysLength = baseFilterKeys.length;
  const savedBaseFilters = filters.filter((filter) => baseFilterKeys.includes(filter.filterKey));

  return filters.length > baseFilterKeysLength && savedBaseFilters.length === baseFilterKeysLength;
};

export const getPeriodCountryOnChange = (stateTimeFrame: string, mainFilters: any, pageName: PageName): Periodicity => {
  const latestFilter = mainFilters as MainFilters;
  if (stateTimeFrame === 'Weekly' && !latestFilter?.timeFrame?.weekly) {
    return Periodicity.Monthly;
  }
  if (stateTimeFrame === 'Monthly' && !latestFilter?.timeFrame?.monthly) {
    return Periodicity.Weekly;
  }

  if (
    (stateTimeFrame === Periodicity.RollingQuarter || stateTimeFrame === Periodicity.CalendarQuarter) &&
    !isValidPageToDisplayPeriod(pageName)
  ) {
    return Periodicity.Monthly;
  }
  return stateTimeFrame as Periodicity;
};

export const isProductPageFiltersInvalid = ({ pageType, sideFiltersState }: SelectedPgCheckParam): boolean => {
  if (pageType !== PageType.RI_PRODUCT) {
    return false;
  }

  const productsAppliedFilters = sideFiltersState.appliedFilters[PageType.RI_PRODUCT].selectedFilters;
  if (productsAppliedFilters.length <= 0) {
    return true;
  }

  const productGroupFilter = productsAppliedFilters.find(byFilterKey(FilterKeys.PRODUCT_GROUP));
  if (!productGroupFilter) {
    return true;
  }

  return productGroupFilter.options.length === 0;
};

/*
 * Check selected product group is available for a given page, if not exits, will show product group modal
 * PageType has to be explicitly passed in from the method caller as if page refreshes, pagesConfig.pageType would reset to default value
 */
export const checkSelectedPgExists = (selectedPgCheckParam: SelectedPgCheckParam) => {
  const { pagesConfig, pageName, pageType, sideFiltersState, setShowPgModal } = selectedPgCheckParam;
  if (!sideFiltersState?.appliedFilters[pageType]) {
    setShowPgModal(true);
    return;
  }

  // If no applied filters yet, modal should render
  if (
    !isBaseFiltersApplied({ pageType, pageName }, sideFiltersState.appliedFilters) ||
    isProductPageFiltersInvalid(selectedPgCheckParam)
  ) {
    setShowPgModal(true);
    return;
  }

  if (!checkIsRetailerSame(pagesConfig)) {
    setShowPgModal(false);
    return;
  }

  const { productDataType, productType, selectedProductGroup, appliedCountries, timeFrame } = selectedPgCheckParam;
  checkPgAndSetModal({
    pgsByPage: pagesConfig.pgsByPage,
    productType: productType,
    productDataType: productDataType,
    selectedProductGroup: selectedProductGroup,
    setShowPgModal: setShowPgModal,
    pagesConfig: pagesConfig,
    appliedCountries: appliedCountries,
    timeFrame: timeFrame,
  });
};

/*
 * checks if the country exist in the subscription or not
 */
export const checkIfCountryExist = (appliedCountries: Option[], pagesConfig: PagesConfig, productType: ProductType) => {
  const { currentClientCountries } = getSubscriptionDetailsByProductType(productType, pagesConfig);
  const currentCountries = currentClientCountries.flatMap((e) => e.id);
  const validCountries = currentCountries.find((e) => e === appliedCountries[0]?.value);
  return !!validCountries;
};

type PgAndSetModalParam = {
  pgsByPage: ProductGroups[];
  productType: ProductType;
  productDataType: ProductDataType;
  selectedProductGroup: Option[] | undefined;
  setShowPgModal: Dispatch<SetStateAction<boolean>>;
  pagesConfig: PagesConfig;
  appliedCountries: Option[];
  timeFrame: Periodicity[];
};

const checkPgAndSetModal = (param: PgAndSetModalParam) => {
  const {
    productType,
    productDataType,
    selectedProductGroup,
    setShowPgModal,
    pagesConfig,
    appliedCountries,
    timeFrame,
  } = param;

  const countryExist = checkIfCountryExist(appliedCountries, pagesConfig, productType);
  if (!countryExist) {
    setShowPgModal(true);
    return;
  }
  const subscriptionDetails = getSubscriptionDetailsByProductType(productType, pagesConfig);
  const currentClientId = subscriptionDetails.currentClientId;
  //Will call to API for every time compare
  if (selectedProductGroup && selectedProductGroup.length > 0) {
    gtPgsByCountryAndTimeFrame(
      currentClientId,
      appliedCountries,
      timeFrame,
      productType,
      productDataType,
      selectedProductGroup[0].value,
      setShowPgModal,
    );
  } else {
    setShowPgModal(true);
  }
  // }
};

/*
 * As we are not using pgsByPage anymore, insteads we will always refer to the response of the api
 * In this function, we call the /product-group api once with the currently applied country and time frame
 * We use the response to perform a check to see if we should render the pg modal or not
 */
const gtPgsByCountryAndTimeFrame = async (
  clientId: number,
  appliedCountries: Option[],
  timeFrame: Periodicity[],
  productType: ProductType,
  productDataType: ProductDataType,
  selectedProductGroup: string,
  setShowPgModal: Dispatch<SetStateAction<boolean>>,
) => {
  // we need to call all relevant periodicities for that country, as pg modal should only appear if pg does not exist in all the periodicities
  if (appliedCountries.length > 1) {
    return;
  }

  const response = await getProductGroupsByCountry(clientId, appliedCountries, productType, productDataType);

  const pgList = response.filter((e) => e.country === appliedCountries[0].value).flatMap((pg) => pg.filter.options);
  if (pgList) {
    const pgFound: boolean = pgList.some((option) => option.value === selectedProductGroup);
    setShowPgModal(!pgFound);
  } else {
    setShowPgModal(true);
  }
};

/*
 * Retailer Overview & Category Leaderboard is sharing the same pageType - PageType.RETAILER_OVERVIEW
 * OR & PA is sharing the same pageType - PageType.OPPORTUNITY_REVIEW
 */
export const constructCountryOptionForSideMenu = (
  pagesConfigAllFilters: SelectedFilters[],
  defaultCountry: Country,
  pageTypes: PageType[],
): void => {
  pageTypes.forEach((pageType: PageType) => {
    const countryObject: Filters = mapCountryObjForFilters(defaultCountry);
    let currentSelectedFilters = pagesConfigAllFilters[pageType].selectedFilters;

    const countryIndex = currentSelectedFilters.findIndex(
      (selectedFilters) => selectedFilters.filterKey === countryObject.filterKey,
    );

    if (
      [
        PageType.TRADING_ASSISTANT,
        PageType.OPPORTUNITY_REVIEW,
        PageType.PERFORMANCE_ANALYSIS,
        PageType.EXPLORE,
        PageType.RI_PRODUCT,
      ].includes(pageType)
    ) {
      if (countryIndex !== -1) {
        // Update country selection only when it has previous country selection
        currentSelectedFilters.splice(countryIndex, 1, countryObject);

        if (pageType === PageType.PERFORMANCE_ANALYSIS) {
          const currencyToUpdate = constructCurrencyFilter(
            // Wrapped inside an array to match parameter type
            [getCurrencyOption(defaultCountry)],
          );
          updateAppliedFilters(currencyToUpdate, currentSelectedFilters);
        }
      }
    } else {
      countryIndex !== -1
        ? currentSelectedFilters.splice(countryIndex, 1, countryObject)
        : currentSelectedFilters.push(countryObject);
    }
  });
};

/*
 * Construct the retailer list based on the subscriptions
 * Scenario 1: Multi-banner, different retailer (clientId)
 * Scenario 2: Multiple countries subscriptions, same retailer (clientId) -> remove duplicate retailer
 */
export const constructRetailerList = (subscriptions: ClientSubscription[]): number[] => {
  return uniqBy(subscriptions, 'retailer').map((subscription) => {
    return subscription.retailer;
  });
};

// Display overlay with loading icon and call Filters API
export const getFiltersForClient = async (
  defaultClientId: number,
  subscribedCountries: PagesConfigCountry[],
  setLoadingOverlay: Dispatch<SetStateAction<boolean>>,
  productType: ProductType,
  productDataType: ProductDataType,
  productGroups: Option[],
): Promise<MainFilters[]> => {
  setLoadingOverlay(true);
  const filtersData = await getFilters({
    clientId: defaultClientId,
    countries: subscribedCountries,
    collectionDataType: undefined,
    productType: productType,
    productDataType: productDataType,
    productGroups: productGroups,
  });
  setLoadingOverlay(false);
  return validateFilters(filtersData);
};

export const findDefaultFilterBySelectedCountries = (filters: Filters[], defaultFilters: MainFilters[]) => {
  const appliedCountry = filters.find((e) => e.filterKey === FilterKeys.COUNTRY)?.options;

  const mainFilters = defaultFilters.find((e) =>
    appliedCountry?.every((c) => {
      return c.value === e.country;
    }),
  );
  // will return first value from default filters(redux) if mainFilters is empty
  return mainFilters ?? defaultFilters[0];
};

export const validateRetailerChange = (
  isRetailerChanged: boolean,
  currFeatureForNewRetailer: FeatureOption,
  currFeature: FeatureOption,
): FeatureOption => {
  if (isRetailerChanged) {
    return currFeatureForNewRetailer;
  }
  return currFeature;
};

export const getProductGroupFromSideFilters = (sideFilters: SelectedFilters) => {
  return sideFilters?.selectedFilters.find((selectedFilter) => selectedFilter.filterKey === FilterKeys.PRODUCT_GROUP);
};

export const getCountryFromSideFilters = (sideFilters: SelectedFilters) => {
  return sideFilters?.selectedFilters.find((selectedFilter) => selectedFilter.filterKey === FilterKeys.COUNTRY);
};

export const getCurrencyFromSideFilters = (sideFilters: SelectedFilters) => {
  return sideFilters?.selectedFilters.find((selectedFilter) => selectedFilter.filterKey === FilterKeys.CURRENCY);
};

export const segmentsFilterWithExcludeFilterKeys = (
  selectedSegments: SelectedFilters,
  segmentsForExclude: FilterKeys[],
) => {
  if (selectedSegments) {
    return selectedSegments.selectedFilters.filter((segments) => {
      return !segmentsForExclude.includes(segments.filterKey);
    });
  }
};
export const concatenateDataTypeNameWithSelectedSegmentType = (
  filters?: Filters[],
  shouldReturnNoOptions: boolean = true,
): string | undefined => {
  if (filters?.length) {
    return filters.map((filter) => filter.dataType).join(', ');
  }

  if (shouldReturnNoOptions) {
    return NO_OPTIONS;
  }

  return undefined;
};

export const getUpdateMainFilters = (
  rawProductGroupResponse: CountryFilterPair[],
  selectedCountry: Option,
  selectedProductGroup: Option,
  initialFilters: MainFilters[],
): {
  chosenTimeFrame: Periodicity;
  chosenTimePeriod: BaseTimePeriod;
} => {
  const filteredRawProductGroups = rawProductGroupResponse.filter(({ country }) => country === selectedCountry.value);
  const availableTimeFrames: string[] = filteredRawProductGroups
    .filter(({ filter }) =>
      filter.options.some(({ value: productGroup }) => productGroup === selectedProductGroup.value),
    )
    .map(({ periodType }) => startCase(periodType.toLowerCase()));

  const emptyTimePeriod: BaseTimePeriod = {
    label: '',
    value: '',
    lastUpdatedDate: 0,
  };
  if (availableTimeFrames.length === 0) {
    return {
      chosenTimeFrame: Periodicity.Weekly,
      chosenTimePeriod: emptyTimePeriod,
    };
  }

  const chosenTimeFrame = availableTimeFrames.includes(Periodicity.Weekly) ? Periodicity.Weekly : Periodicity.Monthly;
  const initialFilter = initialFilters.find(({ country }) => country === selectedCountry.value);
  let chosenTimePeriod: BaseTimePeriod = emptyTimePeriod;
  if (initialFilter) {
    chosenTimePeriod =
      chosenTimeFrame === Periodicity.Weekly
        ? initialFilter.timePeriod.weekly[getLatestPeriod(initialFilter.timePeriod.weekly)]
        : initialFilter.timePeriod.monthly[getLatestPeriod(initialFilter.timePeriod.monthly)];
  }

  return { chosenTimeFrame: chosenTimeFrame, chosenTimePeriod: chosenTimePeriod };
};

/*
 * function to get the first product group in the list to be set as default pg
 * also gets the correct time frame
 */
export const getFirstProductGroup = (
  rawProductGroupResponse: CountryFilterPair[],
  selectedCountry: any,
  chosenTimeFrame: string,
  initialFilters: any,
) => {
  const filteredRawProductGroups = rawProductGroupResponse.filter((e: any) => e.country === selectedCountry.value);
  const productGroupOptions = filteredRawProductGroups.filter(
    (e: any) => e.periodType.toLowerCase() === chosenTimeFrame.toLowerCase(),
  );
  let firstProductGroup = { label: '', value: '' };

  if (productGroupOptions.length !== 0) {
    const sortedProductGroups = sortOptionsFromApi(productGroupOptions[0].filter.options);
    if (sortedProductGroups.length !== 0) {
      firstProductGroup = sortedProductGroups[0];
    }
  }

  const chosenValues = getUpdateMainFilters(
    rawProductGroupResponse,
    selectedCountry,
    firstProductGroup,
    initialFilters,
  );

  return { chosenValues: chosenValues, firstProductGroup: firstProductGroup };
};

/*
 * Gets all the product groups for each country based on subscription.
 * Filters and get unique Pgs for each country, also stores them in pagesConfig.pgsByPage
 */
export const generateProductGroupsByPage = async (pagesConfig: PagesConfig) => {
  const getDataBySubscriptionDetail = async ({
    subscriptionDetail,
    productType,
    productDataType,
  }: {
    subscriptionDetail: SubscriptionDetail;
    productType: ProductType;
    productDataType: ProductDataType;
  }) => {
    if (subscriptionDetail.currentClientCountries && subscriptionDetail.currentClientCountries.length > 0) {
      const currentClientId = subscriptionDetail.currentClientId;

      return getProductGroupsByCountry(
        currentClientId,
        subscriptionDetail.currentClientCountries,
        productType,
        productDataType,
      );
    }
  };

  const performanceSubscriptionDetails = getSubscriptionDetailsByProductType(
    ProductType.PerformanceAnalyzer,
    pagesConfig,
  );
  const productSubscriptionDetails = getSubscriptionDetailsByProductType(ProductType.ProductAnalyzer, pagesConfig);
  const marketPremiumSubscriptionDetails = getSubscriptionDetailsByProductType(ProductType.MarketPremium, pagesConfig);
  const sellerInsightsSubscriptionDetails = getSubscriptionDetailsByProductType(
    ProductType.SellerInsights,
    pagesConfig,
  );
  const retailKadSubscriptionDetails = getSubscriptionDetailsByProductType(ProductType.RetailKad, pagesConfig);

  let dataForPerformance: CountryFilterPair[] = [];
  let dataForProduct: CountryFilterPair[] = [];
  let uniquePerformanceOptions: Option[];
  let uniqueProductOptions: Option[];

  const performanceResponseData = await getDataBySubscriptionDetail({
    subscriptionDetail: performanceSubscriptionDetails,
    productType: ProductType.PerformanceAnalyzer,
    productDataType: ProductDataType.PerformanceAnalyzer,
  });
  if (performanceResponseData) {
    dataForPerformance = [...dataForPerformance, ...performanceResponseData];
  }

  const retailKadPerfResponseData = await getDataBySubscriptionDetail({
    subscriptionDetail: retailKadSubscriptionDetails,
    productType: ProductType.RetailKad,
    productDataType: ProductDataType.PerformanceAnalyzer,
  });
  if (retailKadPerfResponseData) {
    dataForPerformance = [...dataForPerformance, ...retailKadPerfResponseData];
  }

  const marketPremiumResponseData = await getDataBySubscriptionDetail({
    subscriptionDetail: marketPremiumSubscriptionDetails,
    productType: ProductType.MarketPremium,
    productDataType: ProductDataType.PerformanceAnalyzer,
  });
  if (marketPremiumResponseData) {
    dataForPerformance = [...dataForPerformance, ...marketPremiumResponseData];
  }

  let combinedPerformanceOptions: Option[] = dataForPerformance.flatMap((filter) => filter.filter.options);
  uniquePerformanceOptions = combinedPerformanceOptions.filter(
    (option, index) => index === combinedPerformanceOptions.findIndex((e) => e.value === option.value),
  );

  const productResponseData = await getDataBySubscriptionDetail({
    subscriptionDetail: productSubscriptionDetails,
    productType: ProductType.ProductAnalyzer,
    productDataType: ProductDataType.ProductAnalyzer,
  });
  if (productResponseData) {
    dataForProduct = [...dataForProduct, ...productResponseData];
  }

  const sellerInsightsResponseData = await getDataBySubscriptionDetail({
    subscriptionDetail: sellerInsightsSubscriptionDetails,
    productType: ProductType.SellerInsights,
    productDataType: ProductDataType.ProductAnalyzer,
  });
  if (sellerInsightsResponseData) {
    dataForProduct = [...dataForProduct, ...sellerInsightsResponseData];
  }

  const retailKadProdResponseData = await getDataBySubscriptionDetail({
    subscriptionDetail: retailKadSubscriptionDetails,
    productType: ProductType.RetailKad,
    productDataType: ProductDataType.ProductAnalyzer,
  });
  if (retailKadProdResponseData) {
    dataForProduct = [...dataForProduct, ...retailKadProdResponseData];
  }

  let combinedProductOptions: Option[] = dataForProduct.flatMap((filter) => filter.filter.options);
  uniqueProductOptions = combinedProductOptions.filter(
    (option, index) => index === combinedProductOptions.findIndex((e) => e.value === option.value),
  );

  pagesConfig.setPgsByPage([
    {
      //not in use
      pageType: PageType.CATEGORY_LEADERBOARD,
      option: [],
    },
    {
      //not in use
      pageType: PageType.RETAILER_OVERVIEW,
      option: [],
    },
    {
      pageType: PageType.OPPORTUNITY_REVIEW,
      option: [],
    },
    {
      pageType: PageType.TRADING_ASSISTANT,
      option: uniquePerformanceOptions,
    },
    {
      //not in use
      pageType: PageType.PERFORMANCE_ANALYSIS,
      option: [],
    },
    {
      pageType: PageType.RI_PRODUCT,
      option: uniqueProductOptions,
    },
    {
      pageType: PageType.EXPLORE,
      option: [],
    },
  ]);

  return {
    dataForPerformance,
    dataForProduct,
    uniquePerformanceOptions,
    uniqueProductOptions,
  };
};

/**
 * Get all subscriptions by country
 *
 * @param pagesConfig
 * @param appliedCountries
 * @param productType
 * @returns always an array
 */
export const getSubscriptionByCountryAndProductType = (
  pagesConfig: PagesConfig,
  appliedCountries: Option[],
  productType: ProductType,
): ClientSubscription[] => {
  const subscriptionDetails = getSubscriptionDetailsByProductType(productType, pagesConfig);
  const subscription = subscriptionDetails.currentSubscription.result.restrictions;
  return subscription.filter(
    (clientSub) =>
      clientSub.retailer === subscriptionDetails.currentClientId && clientSub.country === appliedCountries[0]?.value,
  );
};

/**
 * Get all unique periodicities from possibly multiple subscription groups and country
 *
 * Reference: NEO-19241
 * @param pagesConfig
 * @param appliedCountries
 * @param productType - PERFORMANCE_ANALYZER/PRODUCT_ANALYZER
 * @returns always an array
 */
export const getSubscriptionPeriodicitiesByCountryAndProductType = (
  productType: ProductType,
  pagesConfig: PagesConfig,
  appliedCountries: Option[],
): Periodicity[] => {
  const subscriptions = getSubscriptionByCountryAndProductType(pagesConfig, appliedCountries, productType);
  return mergeAndUniqPeriodicitiesFromSubscriptions(subscriptions);
};

/**
 * Get all unique periodicities from possibly multiple subscription groups.
 *
 * Consider 2 subscription groups: one with weekly, one with weekly and monthly.
 * If just flatMap, then will end up with 3 elements [Weekly, Weekly, Monthly].
 * Also considered client code does not have a use case with duplicated periodicities.
 */
const mergeAndUniqPeriodicitiesFromSubscriptions = (subscriptions: ClientSubscription[]): Periodicity[] => {
  const mergePeriodicities = subscriptions.flatMap((subscription) => subscription.periodicities);
  return Array.from(new Set(mergePeriodicities)); // unique the elements inside merged array
};

/**
 * get applied filters based on specific page type and filter key
 * @param pagesConfig applied filters from pages config
 * @param filterKeys selected filter key (e.g. Country/Product Group)
 * @param pageType selected page type (e.g. PageType.RETAILER_OVERVIEW)
 */
export const getAppliedFiltersFromPagesConfig = (
  pagesConfig: PagesConfig,
  filterKeys: FilterKeys,
  pageType: PageType,
) => {
  return pagesConfig.appliedFilters[pageType]?.selectedFilters.find((e) => e.filterKey === filterKeys)?.options;
};

/**
 * get the latest period (exclude YTD and MAT which is at the bottom 2 of the time period list)
 * Product Analyzer won't have YTD and MAT time period option, so the latest period is the last period
 * @param timePeriod time period array for monthly and weekly
 */
export function getLatestPeriod(timePeriod: BaseTimePeriod[]) {
  // when there's no value in time period for first load, return time period length
  if (timePeriod.length === 0) return timePeriod.length;

  // when there's value in time period, filter off YTD and MAT
  const filteredMonths = timePeriod.filter((period) => !isPeriodYTDOrMAT(period));
  return filteredMonths.length - 1;
}

/**
 * Retrieves the latest time period with either "YTD" or "MAT" keyword from the given monthly and
 * weekly periods.
 *
 * @param monthlyPeriods        - An array of monthly time periods.
 * @param weeklyPeriods         - An array of weekly time periods.
 * @param matYtdTimePeriodLabel - The label of the time period to search for, containing either "YTD" or "MAT".
 */
export function getLatestPeriodWithMatYtd(
  monthlyPeriods: BaseTimePeriod[],
  weeklyPeriods: BaseTimePeriod[],
  matYtdTimePeriodLabel: string,
) {
  const keyword = matYtdTimePeriodLabel.includes('YTD') ? 'YTD' : 'MAT';
  const monthlySlice = monthlyPeriods.filter((entry): boolean => entry.label.includes(keyword));
  const weeklySlice = weeklyPeriods.filter((entry): boolean => entry.label.includes(keyword));

  return matYtdTimePeriodLabel.includes('Week') ? weeklySlice[0] : monthlySlice[0];
}

export const getCurrentProductTypeForSavedFilters = (pagesConfig: PagesConfig) => {
  const isAuthorizedSellerInsightsUser = isAuthorized(pagesConfig.sellerInsightsClientSubscription);
  if (isAuthorizedSellerInsightsUser) {
    return ProductType.SellerInsights;
  }

  const isAuthorizedRetailKadUser = isAuthorized(pagesConfig.retailKadClientSubscription);
  if (isAuthorizedRetailKadUser) {
    return ProductType.RetailKad;
  }

  if (PageType.RI_PRODUCT === pagesConfig.pageType) {
    return ProductType.ProductAnalyzer;
  }

  if (PageName.TRADING_ASSISTANT === pagesConfig.pageName) {
    return ProductType.MarketPremium;
  }

  return ProductType.PerformanceAnalyzer;
};

export const checkIsRetailerSame = (pagesConfig: PagesConfig) => {
  return pagesConfig.clientId === pagesConfig.hitlistsClientId;
};

export const checkPGsByCountryPrecondition = (
  pagesConfig: PagesConfig,
  clientId: number,
  timeFrame: Periodicity,
  appliedFilters: SelectedFilters[],
  countries: Option[],
): boolean => {
  return checkPGsByCountryPreconditionWithProductType(
    pagesConfig,
    clientId,
    timeFrame,
    appliedFilters,
    countries,
    ProductType.PerformanceAnalyzer,
  );
};

export const checkPGsByCountryPreconditionWithProductType = (
  pagesConfig: PagesConfig,
  clientId: number,
  timeFrame: Periodicity,
  appliedFilters: SelectedFilters[],
  countries: Option[],
  productType: ProductType,
): boolean => {
  if (clientId === -1 || !isBaseFiltersApplied(pagesConfig.getPageTypeAndName(), appliedFilters) || !timeFrame) {
    return false;
  }
  return checkIfCountryExist(countries, pagesConfig, productType);
};

export const updatePgsByCountryOnCountrySelectWithProductType = async (
  pagesConfig: PagesConfig,
  clientId: number,
  timeFrame: Periodicity,
  filter: FilterObject,
  countries: Option[],
  productType: ProductType,
): Promise<void> => {
  pagesConfig.setSelectedTimeFrame(timeFrame);

  let pgs = await getProductGroupsByCountry(clientId, countries, productType);

  const pgsByCountry = filterPGsByCountry(pgs, countries);

  updateCountryAndPGFilters(pagesConfig, countries, pgsByCountry, filter, productType);
};

/**
 * update pgsByAppliedCountry when there is a country change
 * @param pagesConfig
 * @param clientId
 * @param selectedCountries
 * @param appliedCountries
 * @param productType
 */
export const updatePgsByCountryOnCountryApplyWithProductType = async (
  pagesConfig: PagesConfig,
  clientId: number,
  selectedCountries: Option[],
  appliedCountries: Option[],
  productType: ProductType,
  includeMissingPgsInMasterRecord: boolean = false,
  setPgsByAppliedCountry?: (value: SetStateAction<CountryFilterPair[]>) => void,
): Promise<void> => {
  const pgs = await getProductGroupsByCountry(
    clientId,
    appliedCountries,
    productType,
    undefined,
    includeMissingPgsInMasterRecord,
  );
  const pgsByCountry = filterPGsByCountry(pgs, appliedCountries);
  pagesConfig.setPGsByAppliedCountry(pgsByCountry);
  if (setPgsByAppliedCountry) {
    setPgsByAppliedCountry(pgsByCountry);
  }
};

// resets the side menu to what was last applied
export const removeUnsavedFilters: any = (config: PagesConfig) => {
  if (!isEqual(config.selectedFilters, config.appliedFilters)) {
    config.setSelectedFilters(config.appliedFilters);
    return true;
  }
  return false;
};

// Checks if feature value is empty and return boolean results
export const isAllFeatureValuesEmpty = (featureValue: Filters[]) =>
  !featureValue || featureValue.every((f) => !f.feature);

export const createToUpdateFilter = (
  selectFilter: FilterKeyTitleAndExclusion,
  options: Option[],
  disabled: boolean,
  isExclusion: boolean = false,
) => {
  const { filterTitle, filterKey } = selectFilter;
  return {
    dataType: filterTitle,
    feature: filterTitle,
    filterTitle,
    filterKey,
    options,
    disabled,
    isExclusion: isExclusion,
  };
};

/**
 * A function to update the selected and applied country state by comparing with applied filters.
 *
 * @param selectedCountries selected country state
 * @param setSelectedCountries setter of selected country state
 * @param appliedCountries applied country state
 * @param setAppliedCountries setter of applied country state
 * @param appliedFilters applied filters on specific page (e.g. pagesConfig.appliedFilters)
 */
export const updateSelectedAppliedCountryStateByAppliedFilter = (
  selectedCountries: Option[],
  setSelectedCountries: Dispatch<SetStateAction<Option[]>>,
  appliedCountries: Option[],
  setAppliedCountries: Dispatch<SetStateAction<Option[]>>,
  appliedFilters: Filters[],
) => {
  const ctryFilter = appliedFilters?.find((e) => e.filterKey === FilterKeys.COUNTRY);

  if (ctryFilter) {
    updateFilterOptionsStateByAppliedFilters(selectedCountries, setSelectedCountries, ctryFilter);
    updateFilterOptionsStateByAppliedFilters(appliedCountries, setAppliedCountries, ctryFilter);
  }
};

/**
 * A function to update the selected and applied product group state by comparing with applied filters.
 *
 * @param selectedProductGroup selected product group state
 * @param setSelectedProductGroup setter of selected product group state
 * @param appliedProductGroup applied product group state
 * @param setAppliedProductGroup setter of applied product group state
 * @param appliedFilters applied filters on specific page (e.g. pagesConfig.appliedFilters)
 */
export const updateSelectedAppliedPgStateByAppliedFilter = (
  selectedProductGroup: Option[] | undefined,
  setSelectedProductGroup: Dispatch<SetStateAction<Option[] | undefined>>,
  appliedProductGroup: Option[],
  setAppliedProductGroup: Dispatch<SetStateAction<Option[]>>,
  appliedFilters: Filters[],
) => {
  const pgFilter = appliedFilters?.find((e) => e.filterKey === FilterKeys.PRODUCT_GROUP);

  if (pgFilter) {
    updateFilterOptionsStateByAppliedFilters(selectedProductGroup, setSelectedProductGroup, pgFilter);
    updateFilterOptionsStateByAppliedFilters(appliedProductGroup, setAppliedProductGroup, pgFilter);
  }
};

/**
 * To update the optionsFilter state by comparing optionsFilter (label and value) with appliedFilters (label and value).
 * Conditions state will update:
 * 1 - If onPageFirstLoad flag set to true
 * 2 - optionsFilter label !== appliedFilters label
 * 3 - optionsFilter value !== appliedFilters value
 * 4 - optionsFilter is undefined
 *
 * @param optionsFilter list of optionsFilter state
 * @param setOptionsFilter setter of optionsFilter state
 * @param appliedFilters applied filters on specific page (e.g. pagesConfig.appliedFilters)
 */
export const updateFilterOptionsStateByAppliedFilters = (
  optionsFilter: Option[] | undefined,
  setOptionsFilter: Dispatch<SetStateAction<Option[]>> | Dispatch<SetStateAction<Option[] | undefined>>,
  appliedFilters: Filters,
) => {
  setOptionsFilter(structuredClone(appliedFilters.options));
};

export const emptyBrandFilter = {
  dataType: FilterTitle.BRAND,
  feature: '',
  filterKey: FilterKeys.BRAND,
  filterTitle: FilterTitle.BRAND,
  options: [],
};

export const getFeatureValuesByPeriodicity = (
  weeklyMonthlyFeatures: {
    weekly: Filters[];
    monthly: Filters[];
  },
  periodicity: Periodicity,
) => {
  return getDefaultValueForDerivablePeriodicity(periodicity) === Periodicity.Monthly
    ? weeklyMonthlyFeatures.monthly
    : weeklyMonthlyFeatures.weekly;
};

/**
 * Returns boolean true period, periodicity and comparisonPeriod is empty in MainFiltersState
 * @param mainFiltersState is the redux state for Performance Analysis and Opportunity Review
 */
export const isMainFiltersStatePeriodEmpty = (mainFiltersState: MainFiltersState): boolean => {
  return !mainFiltersState.period && !mainFiltersState.comparisonPeriod && !mainFiltersState.timeFrame;
};

/**
 * Update applied filters with new filter values.
 *
 * @param filtersToUpdate - The new filters to add/update.
 * @param currentSelectedFilters - The currently applied filters.
 */
export const updateAppliedFilters = (filtersToUpdate: Filters, currentSelectedFilters: Filters[]) => {
  const filterIndex = currentSelectedFilters.findIndex((e) => e.filterKey === filtersToUpdate.filterKey);
  filterIndex !== -1
    ? currentSelectedFilters.splice(filterIndex, 1, filtersToUpdate)
    : currentSelectedFilters.push(filtersToUpdate);
};

/**
 * Constructor method to create currency side menu filter options based on the subscription and
 * current active client.
 *
 * @param pagesConfig - A reference to the pagesConfig object.
 * @param productType - The product type of the current page.
 * @param currentClientId - The current active client id.
 */
export const constructCurrencySideMenuFilterOptions = (
  pagesConfig: PagesConfig,
  productType: ProductType,
  currentClientId: number,
): Filters => {
  const defaultCountries: Option[] = DEFAULT_CURRENCY_DROPDOWN_COUNTRIES.map((country) => getCurrencyOption(country));

  const options = getMergeAndUniqObjectArrayByKey(
    defaultCountries,
    getCurrencyOptionsFromSubscription(pagesConfig, productType, currentClientId),
    'value',
  );

  return constructCurrencyFilter(options);
};

/**
 * Constructor method to create a new currency filter object.
 *
 * @param currencyOptions - A list of currency options to be used for the filter.
 */
export const constructCurrencyFilter = (currencyOptions: Option[]): Filters => {
  return {
    dataType: 'CURRENCY',
    feature: 'CURRENCY',
    filterTitle: FilterTitle.CURRENCY,
    filterKey: FilterKeys.CURRENCY,
    options: currencyOptions,
    disabled: false,
  };
};

/**
 * Constructor method to create a new country filter object.
 *
 * @param countryOptions - A list of country options to be used for the filter.
 */

export const constructCountryFilter = (countryOptions: Option[]): Filters => {
  return {
    dataType: 'COUNTRY',
    feature: 'COUNTRY',
    filterTitle: FilterTitle.COUNTRY,
    filterKey: FilterKeys.COUNTRY,
    options: countryOptions,
    disabled: false,
  };
};

export const getCurrencyBySelectedCountry = (pagesConfig: PagesConfig, selectedCountry: Option) => {
  const clientSubscription = findSubscriptionByCountry(pagesConfig, selectedCountry);
  let country = clientSubscription?.countryObject;

  country ??= findCountryFromDefaultCurrenciesByCountryOption(selectedCountry);

  return constructCurrencyFilter([getCurrencyOption(country)]);
};

/**
 * REFACTOR: Can we combine this with constructCurrencyFilter and constructCountryFilter to create
 * a generic constructFilter method?
 */
export const constructProductGroupFilter = (productGroupOptions: Option[], isDisabled: boolean = false): Filters => ({
  dataType: 'PRODUCT GROUP',
  feature: 'PRODUCT GROUP',
  filterTitle: 'Product Group',
  filterKey: FilterKeys.PRODUCT_GROUP,
  options: productGroupOptions,
  disabled: isDisabled,
});

/**
 * Helper method to update the applied filters with the currency filter based on the selected
 * country.
 *
 * @param pagesConfig - A reference to the pagesConfig object.
 * @param selectedCountry - The selected country.
 * @param currentlyAppliedFilters - The currently applied filters.
 */
export const updateAppliedCurrencyFilterByCountry = (
  pagesConfig: PagesConfig,
  selectedCountry: Option,
  currentlyAppliedFilters: Filters[],
) => {
  const currencyToUpdate = getCurrencyBySelectedCountry(pagesConfig, selectedCountry);
  updateAppliedFilters(currencyToUpdate, currentlyAppliedFilters);
};

/**
 * Determines if weekly YTD or MAT periodicity is applied with a currency filter that is not the
 * default currency of the applied country.
 *
 * This check is necessary because weekly YTD and MAT for a specified currency that is not the
 * local default currency for a given applied country cannot be aggregated on the fly due to weekly
 * data limitation.
 *
 * @param pagesConfig - A reference to pagesConfig.
 * @param productType - The current effective product type in use.
 * @param periodicity - The current periodicity (e.g., Monthly, Weekly, etc).
 * @param period - The current period.
 */
export const isWeekYtdOrMatWithoutLocalCurrency = (
  pagesConfig: PagesConfig,
  productType: ProductType,
  periodicity: Periodicity,
  period: BaseTimePeriod,
): boolean => {
  const { appliedFilters, pageType } = pagesConfig;
  const countryFilter = appliedFilters[pageType].selectedFilters.find(byFilterKey(FilterKeys.COUNTRY));
  const currencyFilter = appliedFilters[pageType].selectedFilters.find(byFilterKey(FilterKeys.CURRENCY));

  if (!countryFilter || !currencyFilter) {
    return false;
  }

  return (
    !isLocalCurrencyApplied(pagesConfig, productType, currencyFilter, countryFilter) &&
    isWeeklyYTDOrMAT(periodicity, period)
  );
};

/**
 * Determines if there are no options available.
 *
 * @param options - The options to check.
 */
export const isEmptyOrNoOptions = (options: Option[]) => options.length === 0 || options[0].label === NO_OPTIONS;

export const areOptionsTheSame = (options1: Option[], options2: Option[], optionKey: keyof Option) => {
  const options1ToCompare = options1.map((option) => option[optionKey]);
  const options2ToCompare = options2.map((option) => option[optionKey]);

  if (options1ToCompare.length !== options2ToCompare.length) {
    return false;
  }

  return options1ToCompare.every((option) => options2ToCompare.includes(option));
};

/**
 * To check if saved multi country selection filters exist in current performance analyzer subscription
 * (as now multi country only available in PERFORMANCE ANALYSIS page)
 *
 * @param pagesConfig
 * */
export const isAllSavedMultiCountryFilterExistInCurrentSubscription = (pagesConfig: PagesConfig): boolean => {
  return pagesConfig.multiCountryClientCountries.every((multCountryOption) =>
    pagesConfig.clientCountries.some(
      (currentSubscriptionCountry) => currentSubscriptionCountry.id === multCountryOption.value,
    ),
  );
};

export const isRemovePricebandFilterForNonLocalCurrency = (
  pagesConfig: PagesConfig,
  appliedFilters: Filters[],
  effectiveProductType: ProductType,
  feature: FeatureOption,
) => {
  const currencyFilter = appliedFilters.find(byFilterKey(FilterKeys.CURRENCY));
  const appliedCountry = appliedFilters.find(byFilterKey(FilterKeys.COUNTRY));

  return (
    currencyFilter &&
    appliedCountry &&
    !isLocalCurrencyApplied(pagesConfig, effectiveProductType, currencyFilter, appliedCountry) &&
    feature.filterKey === FilterKeys.PRICEBAND
  );
};

export const updateSortingOptions = (
  appliedProductSpecificationFilters: Filters[],
  pagesConfig: PagesConfig,
  currentSortingOption: SortingOptions,
) => {
  if (
    getIsDisabledStatus(appliedProductSpecificationFilters) &&
    pagesConfig.sortingOptionsProducts.sortDir === SortDirection.asc
  ) {
    pagesConfig.setSortingOptionsProducts({ ...currentSortingOption, sortDir: SortDirection.desc });
  }
};

const constructFilters = (featureOption: FeatureOption): Filters => {
  return {
    dataType: featureOption.label,
    feature: featureOption.label,
    filterTitle: featureOption.value,
    filterKey: featureOption.filterKey,
    options: [
      {
        label: featureOption.label,
        value: featureOption.value,
      },
    ],
    disabled: false,
  };
};

/**
 *Retrieves the list of Filters from the feature selector in Opportunity Review, Performance Analysis
 *and Trading Assistant page. These pages contains charts that have a feature selector.
 *Eg: ShowPerformanceWithin in PA page and ShowOpportunitiesWithin in OR page
 *
 * @param mainFiltersState The mainFilterState which contains the selected feature on the charts
 * @returns A list of applied filters on the charts.
 */
export const getChartFilters = (mainFiltersState: MainFiltersState): Filters[] => {
  let chartFilters: Filters[] = [];
  // Retrieves 'Show XXX within' FeatureSelector value
  if (mainFiltersState?.feature) {
    chartFilters.push(constructFilters(mainFiltersState.feature));
  }
  // Retrieves 'By' FeatureSelector value (Eg: StackBarChart in PA page)
  if (mainFiltersState?.segmentByFeature) {
    chartFilters.push(constructFilters(mainFiltersState.segmentByFeature));
  }
  return chartFilters;
};

export const getBaseFilterOptionsFromResponseConfig = (
  responseConfig: ResponseConfig,
  filterKey: FilterKeys.COUNTRY | FilterKeys.PRODUCT_GROUP,
) => {
  const baseFilters = responseConfig.response[0].filters;
  if (!baseFilters.length) {
    return [];
  }

  const filter = baseFilters.find(byFilterKey(filterKey));
  return filter?.options ?? [];
};

export const getAndReplacePriceBandIndexToPriceBandRange = (
  savedFilter: SavedFilters,
  sideFilterPriceBandList: ProductGroupPriceBandFeatureValue[] | undefined,
  pg: string,
) => {
  savedFilter.filters.forEach((e) => {
    if (e.filterKey === FilterKeys.PRICEBAND) {
      let priceBand = sideFilterPriceBandList
        ?.find((features) => features.oldProductGroup.toUpperCase().includes(pg))
        ?.sideMenuFilterDtoList.filter((filter) => filter.filterKey === FilterKeys.PRICEBAND)[0];
      e.options = priceBand ? priceBand.options : e.options;
    }
  });
  return savedFilter;
};

/**
 * Options is the dropdown options from select component from any page component. e.g Side filter select, Trading Assistant Filter selections.
 * Return true if options is more than or equal 5, else false
 * This method is use to display search box in select component(single or multi) if true, dont display if options less than 5
 */
export const isAllowSearchBox = (options: Option[] | DisplayTypesOption[]): boolean => {
  return options.length >= 5;
};

/**
 * Determines if the exclusion toggle can be shown for the given filter on the current page.
 * Only BRAND on specific pages supports exclusion.
 *
 * @param filterKey - The filter key being rendered.
 * @param currentPage - The current page type.
 * @returns true if exclusion toggle is available for this filter on this page.
 */
export const canShowExclusionToggle = (filterKey: FilterKeys, currentPage: PageType): boolean =>
  filterKey === FilterKeys.BRAND &&
  [PageType.RETAILER_OVERVIEW, PageType.EXPLORE, PageType.OPPORTUNITY_REVIEW, PageType.RI_PRODUCT].includes(
    currentPage,
  );

/**
 * Checks if the exclusion toggle is active for a given filter key on the current page.
 *
 * @param filterKey - The filter key to check for exclusion mode.
 * @param currentPage - The current page type.
 * @param pagesConfig - The current pages config object.
 * @returns True if exclusion mode is enabled for the filter key, otherwise false.
 */
export const isExclusionActiveForFilter = (
  filterKey: FilterKeys,
  currentPage: PageType,
  pagesConfig: PagesConfig,
): boolean =>
  pagesConfig.selectedFilters[currentPage].selectedFilters.find((f) => f.filterKey === filterKey)?.isExclusion === true;

export const isMaxNumOfModelNotNull = (filterObject: FilterObject) => {
  //if maxNumOfModels for pg not null the feature-value api return 'disabled' ===true for 'dataType' BRAND.
  //if 'disabled' true, disable all selection except Base and Market Definition
  return (
    filterObject.mainTitle !== 'Base' &&
    filterObject.mainTitle !== 'Market Definition' &&
    getIsDisabledStatus(productsResponseConfig.response[2].filters)
  );
};
